import type { CSSProperties } from "react";
import { AnimatePresence, motion } from "framer-motion";
import _ from "lodash";
import React from "react";
import { getDefaultTab, useAppRouteNavigation } from "@/app/app/_/routing";
import { Icon } from "@/components/atoms/Icon";
import { Values } from "@/constants";
import { connectionPrefetchDescriptor } from "@/hooks/useConnectionSetup";
import { useHoverPrefetch } from "@/hooks/useHoverPrefetch";
import { icons } from "@/objects/@icons";
import { State } from "@/state";
import { AppViewsType, AppViewTabModeType } from "@/state/@types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/**
 * One tab under an expanded sidebar item.
 *
 * Its own component because the preload is a hook and each tab warms a
 * *different* query: the tab's locked filters (bank feed's
 * ``financialAccount``, posted/unposted, invoices/bills) are part of the
 * variables, so hovering "Posted" must fetch the posted list, not the
 * section's default one.
 */
function AppSidebarRoutingTab({
  view,
  tabKey,
  isActive,
  isLoading,
  isSidebarMinimized,
  onClick,
}: {
  view: AppViewsType;
  tabKey: string;
  isActive: boolean;
  isLoading: boolean;
  isSidebarMinimized: boolean;
  onClick: (tabKey: string) => void;
}) {
  const prefetch = useHoverPrefetch(() =>
    connectionPrefetchDescriptor(view, tabKey),
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-testid={`sidebar-tab-${tabKey}`}
          onClick={() => onClick(tabKey)}
          {...prefetch}
          // A finger's target on a touch screen.
          className={cn(
            "grow pt-1 pb-1 pl-2 pr-2 mt-px h-7 pointer-coarse:h-11",
            isActive ? "bg-secondary" : "bg-transparent",
            "rounded-sm cursor-pointer flex items-center [transition:background-color_0.1s] overflow-hidden justify-start hover:bg-secondary",
          )}
        >
          <Typography
            level="body-md"
            className={cn(
              "text-ellipsis whitespace-nowrap overflow-hidden",
              isActive ? "text-foreground" : "text-muted-foreground",
            )}
          >
            <Skeleton loading={isLoading}>
              {Values.APP_VIEW_TAB_LABELS[tabKey] ??
                _.upperFirst(tabKey.replace("-", " "))}
            </Skeleton>
          </Typography>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        className={cn(!isSidebarMinimized && "hidden")}
      >
        {tabKey}
      </TooltipContent>
    </Tooltip>
  );
}

const AppSidebarRoutingItem = React.memo(function AppSidebarRoutingItem({
  item,
  badgeCount = 0,
  collapsedDotColor = null,
  endAdornment = null,
  onClick = null,
  isLoading = false,
  tooltipTitle = null,
  isActive: isActiveOverride,
  className,
}: {
  item: {
    key: string;
    name: string;
    icon: React.ReactNode;
    path?: string;
  };
  /** Red count on the row; hidden at zero. */
  badgeCount?: number;
  /** Dot over the icon while the sidebar is minimized; hidden when null. */
  collapsedDotColor?: "danger" | "neutral" | null;
  endAdornment?: React.ReactNode;
  onClick?: (() => void) | null;
  isLoading?: boolean;
  tooltipTitle?: string;
  isActive?: boolean;
  /** The row's frame, over its own: a ground and edge of its own, say. */
  className?: string;
}) {
  const activeAppView = State((store) => store.activeAppView);
  const activeAppViewTab = State((store) => store.activeTab);
  const isSidebarMinimized = State((store) => store.isSidebarMinimized);
  // const setActiveTab = State((store) => store.setActiveTab);
  const availableTabs: readonly string[] =
    Values.APP_VIEW_TAB_MODE_MAP[
      item.key as keyof typeof Values.APP_VIEW_TAB_MODE_MAP
    ]?.values || [];

  const hasTabs = availableTabs.length > 0;
  const [isOpen, setIsOpen] = React.useState(false);
  const { navigateToView } = useAppRouteNavigation();

  const isSectionActive =
    isActiveOverride ??
    (activeAppView === item.key &&
      (!hasTabs ||
        availableTabs.some((tabKey) => tabKey === activeAppViewTab)));

  const [iconHoverExtra] = React.useState(() => {
    const scale = 1 + Math.random() * 0.1;
    const rotate = (Math.random() * 2 - 1) * 5;
    return `scale(${scale.toFixed(3)}) rotate(${rotate.toFixed(1)}deg)`;
  });
  const [isRowHovered, setIsRowHovered] = React.useState(false);
  // Clicking the row lands on the view's default tab with filters cleared, so
  // that is the list to start early. Returns null for items that are not list
  // views (settings entities, external links).
  const prefetchRow = useHoverPrefetch(() =>
    connectionPrefetchDescriptor(
      item.key as AppViewsType,
      getDefaultTab(item.key as AppViewsType),
    ),
  );
  const handleRowMouseEnter = React.useCallback(() => {
    setIsRowHovered(true);
    prefetchRow.onMouseEnter();
  }, [prefetchRow]);
  const handleRowMouseLeave = React.useCallback(() => {
    setIsRowHovered(false);
    prefetchRow.onMouseLeave();
  }, [prefetchRow]);

  // A group - an item with tabs - opens and shuts, which of its tabs is wanted
  // being the person's to pick. Folded to its icons, the sidebar stays folded
  // and the group's first tab is opened, as its icon is all there is to click.
  const handleMainClick = React.useCallback(() => {
    if (hasTabs && isSidebarMinimized) {
      navigateToView(item.key as AppViewsType, {
        tab: availableTabs[0] as AppViewTabModeType,
        preservePanel: true,
        preserveQuery: false,
      });
    } else if (hasTabs) {
      setIsOpen(!isOpen);
    } else if (onClick) {
      onClick();
    } else {
      navigateToView(item.key as AppViewsType, {
        preservePanel: true,
        preserveQuery: false,
      });
    }
  }, [
    onClick,
    hasTabs,
    availableTabs,
    item.key,
    isOpen,
    isSidebarMinimized,
    navigateToView,
  ]);

  const handleTabClick = React.useCallback(
    (tabKey: string) => {
      navigateToView(item.key as AppViewsType, {
        tab: tabKey as AppViewTabModeType,
        preservePanel: true,
        preserveQuery: false,
      });
    },
    [item.key, navigateToView],
  );

  const handleChevronClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    },
    [isOpen],
  );

  const chevronButton = hasTabs ? (
    <Button
      variant="ghost"
      size="icon-xs"
      className="bg-transparent [transition:transform_0.2s_ease-in-out] origin-center hover:bg-transparent"
      onClick={handleChevronClick}
    >
      <Icon
        ReactIcon={icons.chevronDown}
        style={{
          transform: !isOpen ? "rotate(0deg)" : "rotate(180deg)",
          translate: "0 1px",
          transition: "transform 0.2s ease-in-out",
        }}
      />
    </Button>
  ) : null;

  return (
    <>
      <div className={cn("w-full mb-px pl-2 pr-2 mt-px", className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              data-testid={`sidebar-nav-${item.key}`}
              onClick={handleMainClick}
              onMouseEnter={handleRowMouseEnter}
              onMouseLeave={handleRowMouseLeave}
              className={cn(
                "pt-1 pb-1 w-full h-7 rounded-sm cursor-pointer flex items-center [transition:background-color_0.1s] overflow-visible justify-start relative hover:bg-secondary",
                // A finger's target on a touch screen.
                "pointer-coarse:h-11",
                (isSectionActive && !isOpen) || (isSectionActive && !hasTabs)
                  ? "bg-secondary"
                  : "bg-transparent",
                hasTabs ? "pr-1" : "pr-2",
              )}
            >
              {collapsedDotColor && (
                <span
                  aria-hidden
                  // Inside the minimized row, which the sidebar's scroller
                  // clips, and above the icon, whose hover transform lifts it
                  // onto its own layer.
                  className={cn(
                    "hidden absolute top-1 left-5.5 z-1 w-1.75 h-1.75 rounded-[50%]",
                    collapsedDotColor === "neutral"
                      ? "bg-primary"
                      : collapsedDotColor === "danger"
                        ? "bg-destructive"
                        : "bg-[undefined.solidBg]",
                    "pointer-events-none [@container_(max-width:_50px)]:block",
                  )}
                />
              )}
              <div
                // Flex so the inline SVG is not baseline-aligned, which sat it
                // a couple of pixels below the row's centre line.
                className={cn(
                  "ml-[9.6px] mr-2 flex items-center",
                  isRowHovered
                    ? "transform-(--transform)"
                    : "transform-[scale(1.2)]",
                  "origin-center [transition:transform_0.2s_ease-out]",
                )}
                style={
                  {
                    "--transform": isRowHovered
                      ? `scale(1.2) ${iconHoverExtra}`
                      : undefined,
                  } as CSSProperties
                }
              >
                {item.icon}
              </div>
              <Typography
                level="body-md"
                className="truncate grow-0 min-w-0 w-min max-w-full text-ellipsis items-center whitespace-nowrap mr-1 [@container_(max-width:_50px)]:hidden"
              >
                <Skeleton loading={isLoading}>{item.name}</Skeleton>
              </Typography>
              <span className="inline-flex items-center [@container_(max-width:_50px)]:hidden">
                {chevronButton}
              </span>
              <div className="grow" />
              {badgeCount > 0 && (
                <span
                  aria-label={`${badgeCount} to review`}
                  className="min-w-4.5 h-4.5 pl-1.25 pr-1.25 rounded inline-flex items-center justify-center text-[11px] font-semibold [line-height:1] [font-variant-numeric:tabular-nums] pointer-events-none text-destructive bg-destructive/10 ml-1 [@container_(max-width:_50px)]:hidden"
                >
                  {badgeCount > 99 ? "99+" : badgeCount}
                </span>
              )}
              {endAdornment && (
                <>
                  <div className="flex items-center justify-center origin-center [@container_(max-width:_50px)]:hidden">
                    {endAdornment}
                  </div>
                </>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            className={cn(!isSidebarMinimized && "hidden")}
          >
            {tooltipTitle || item.name}
          </TooltipContent>
        </Tooltip>
      </div>

      <AnimatePresence>
        {isOpen && hasTabs && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{
              overflow: "hidden",
              paddingLeft: "8px",
              width: "100%",
              maxWidth: "100%",
              paddingRight: "8px",
              // minHeight: "max-content",
            }}
          >
            <div className="flex w-full max-w-full min-h-max [@container_(max-width:_50px)]:hidden">
              <div className="min-w-px bg-border ml-4 mr-2" />
              <div className="grow max-w-[calc(100%-16px-8px-1px)]">
                {availableTabs.map((tabKey) => (
                  <AppSidebarRoutingTab
                    key={tabKey}
                    view={item.key as AppViewsType}
                    tabKey={tabKey}
                    isActive={
                      activeAppView === item.key && activeAppViewTab === tabKey
                    }
                    isLoading={isLoading}
                    isSidebarMinimized={isSidebarMinimized}
                    onClick={handleTabClick}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

export { AppSidebarRoutingItem as RoutingItem };
